import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calculator, ArrowRight, Loader2, Droplets, Wifi, HelpCircle, MapPin } from 'lucide-react';
import PlacesInput from './PlacesInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PriceCalculator = () => {
  const { t, language } = useLanguage();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [price, setPrice] = useState(null);
  const [priceDetails, setPriceDetails] = useState(null);
  const [durationText, setDurationText] = useState('');

  const [formData, setFormData] = useState({
    service_type: 'standard_premium',
    booking_type: 'transfer',
    route_id: '',
    custom_distance: '',
    hours: 4,
    passengers: 1,
    pickup_location: '',
    dropoff_location: '',
    hourly_location: '',
    hourly_destination: '',
  });

  const [hourlyOutsideZurich, setHourlyOutsideZurich] = useState(false);

  useEffect(() => {
    fetchRoutes();
  }, []);

  // Auto-calculate distance when both addresses are filled
  useEffect(() => {
    if (formData.booking_type === 'transfer' && formData.pickup_location.length > 3 && formData.dropoff_location.length > 3) {
      const timer = setTimeout(() => {
        calculateDistance();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [formData.pickup_location, formData.dropoff_location, formData.booking_type]);

  const calculateDistance = async () => {
    setDistanceLoading(true);
    try {
      const response = await axios.post(`${API}/calculate-distance`, {
        origin: formData.pickup_location,
        destination: formData.dropoff_location,
      });
      const km = response.data.distance_km;
      setFormData(prev => ({ ...prev, custom_distance: String(Math.round(km)) }));
      setDurationText(response.data.duration_text);
    } catch (error) {
      console.error('Failed to calculate distance:', error);
    } finally {
      setDistanceLoading(false);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await axios.get(`${API}/routes`);
      setRoutes(response.data);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    }
  };

  const getRouteName = (route) => {
    switch (language) {
      case 'de': return route.name_de;
      case 'ro': return route.name_ro;
      default: return route.name_en;
    }
  };

  const isZurichArea = (location) => {
    const lower = location.toLowerCase();
    return lower.includes('zurich') || lower.includes('zürich') || lower.includes('zurigo') || lower.includes('kloten') || lower.includes('winterthur') || lower.includes('dietikon') || lower.includes('dübendorf') || lower.includes('opfikon') || lower.includes('wallisellen') || lower.includes('uster');
  };

  const handleCalculate = async () => {
    // Check hourly Zurich restriction (both pickup and destination)
    if (formData.booking_type === 'hourly') {
      const pickupMissing = !formData.hourly_location;
      const destMissing = !formData.hourly_destination;
      const pickupOutside = !pickupMissing && !isZurichArea(formData.hourly_location);
      const destOutside = !destMissing && !isZurichArea(formData.hourly_destination);
      if (pickupMissing || destMissing || pickupOutside || destOutside) {
        setHourlyOutsideZurich(true);
        setPrice(null);
        setPriceDetails(null);
        return;
      }
      setHourlyOutsideZurich(false);
    }

    setLoading(true);
    try {
      const payload = {
        service_type: formData.service_type,
        booking_type: formData.booking_type,
        route_id: formData.route_id || null,
        custom_distance: formData.custom_distance ? parseFloat(formData.custom_distance) : null,
        hours: formData.booking_type === 'hourly' ? formData.hours : null,
        passengers: formData.passengers,
      };

      const response = await axios.post(`${API}/calculate-price`, payload);
      setPrice(response.data.price);
      setPriceDetails(response.data.details);
    } catch (error) {
      console.error('Failed to calculate price:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRoutes = routes.filter(route => route.is_airport && route.id !== 'custom');

  return (
    <section id="calculator" className="py-24 bg-[#0A0A0A]" data-testid="price-calculator">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 border border-[#D4AF37] text-[#D4AF37] mb-6">
            <Calculator className="w-8 h-8" />
          </div>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="calculator-title"
          >
            {t('calc_title')}
          </h2>
          <p className="text-[#A1A1AA] max-w-xl mx-auto">
            {t('calc_subtitle')}
          </p>
        </div>

        {/* Calculator Form */}
        <div className="bg-[#121212] border border-white/10 p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Service Type */}
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2">
                {t('calc_service_type')}
              </label>
              <Select
                value={formData.service_type}
                onValueChange={(value) => setFormData({ ...formData, service_type: value })}
              >
                <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="service-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="standard_premium" className="text-white hover:bg-white/10">
                    {t('standard_premium')}
                  </SelectItem>
                  <SelectItem value="business" className="text-white hover:bg-white/10">
                    {t('business')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Booking Type */}
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2">
                {t('calc_booking_type')}
              </label>
              <Select
                value={formData.booking_type}
                onValueChange={(value) => setFormData({ ...formData, booking_type: value, route_id: '' })}
              >
                <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="booking-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="transfer" className="text-white hover:bg-white/10">
                    {t('calc_airport_transfer')}
                  </SelectItem>
                  <SelectItem value="hourly" className="text-white hover:bg-white/10">
                    {t('calc_hourly_booking')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* From / To / Distance (for regular transfer) */}
            {formData.booking_type === 'transfer' && (
              <>
                <PlacesInput
                  value={formData.pickup_location || ''}
                  onChange={(val) => setFormData({ ...formData, pickup_location: val })}
                  placeholder={t('route_from_placeholder')}
                  label={t('route_from')}
                  testId="calc-pickup"
                />
                <PlacesInput
                  value={formData.dropoff_location || ''}
                  onChange={(val) => setFormData({ ...formData, dropoff_location: val })}
                  placeholder={t('route_to_placeholder')}
                  label={t('route_to')}
                  testId="calc-dropoff"
                />
                <div className="md:col-span-2">
                  <label className="block text-sm text-[#A1A1AA] mb-2">
                    {t('calc_custom_distance')}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="20"
                      max="300"
                      value={formData.custom_distance}
                      readOnly
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] outline-none"
                      placeholder={distanceLoading ? "..." : "km"}
                      data-testid="custom-distance-input"
                    />
                    {distanceLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
                      </div>
                    )}
                    {!distanceLoading && durationText && formData.custom_distance && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#D4AF37]">
                        ~{durationText}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Hours (for hourly booking) */}
            {formData.booking_type === 'hourly' && (
              <>
                <PlacesInput
                  value={formData.hourly_location || ''}
                  onChange={(val) => { setFormData({ ...formData, hourly_location: val }); setHourlyOutsideZurich(false); }}
                  placeholder={t('hourly_location_placeholder') || 'e.g. Zurich, Kloten, Winterthur...'}
                  label={t('hourly_location_label') || 'Pickup Area'}
                  testId="calc-hourly-location"
                />
                <PlacesInput
                  value={formData.hourly_destination || ''}
                  onChange={(val) => { setFormData({ ...formData, hourly_destination: val }); setHourlyOutsideZurich(false); }}
                  placeholder={t('hourly_destination_placeholder') || 'e.g. Zurich, Kloten, Winterthur...'}
                  label={t('hourly_destination_label') || 'Destination Area'}
                  testId="calc-hourly-destination"
                />
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                    {t('calc_hours')}
                    <span className="relative group">
                      <HelpCircle className="w-4 h-4 text-[#D4AF37] cursor-help" />
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#1a1a1a] border border-[#D4AF37]/30 text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                        {t('calc_hours_min_tooltip')}
                      </span>
                    </span>
                  </label>
                  <input
                    type="number"
                    min="4"
                    max="12"
                    value={formData.hours}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 4;
                      setFormData({ ...formData, hours: val < 4 ? 4 : val });
                    }}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
                    data-testid="hours-input"
                  />
                </div>
              </>
            )}

            {/* Passengers */}
            <div className="md:col-span-2">
              <label className="block text-sm text-[#A1A1AA] mb-2">
                {t('calc_passengers')}
              </label>
              <Select
                value={String(formData.passengers)}
                onValueChange={(value) => setFormData({ ...formData, passengers: parseInt(value) })}
              >
                <SelectTrigger className="w-full bg-[#0A0A0A] border-white/10 text-white" data-testid="passengers-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                    <SelectItem key={num} value={String(num)} className="text-white hover:bg-white/10">
                      {num} {num === 1 ? 'Passenger' : 'Passengers'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Calculate Button */}
          <button
            onClick={handleCalculate}
            disabled={loading || (formData.booking_type === 'airport_transfer' && !formData.route_id) || (formData.booking_type === 'transfer' && !formData.custom_distance)}
            className="w-full mt-8 py-4 bg-[#D4AF37] text-black font-semibold text-sm uppercase tracking-wider hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="calculate-price-button"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Calculator className="w-5 h-5" />
                {t('calc_calculate')}
              </>
            )}
          </button>

          {/* Hourly - Outside Zurich Message */}
          {hourlyOutsideZurich && (
            <div className="mt-8 p-6 bg-[#0A0A0A] border border-[#D4AF37]/30 text-center" data-testid="hourly-zurich-message">
              <p className="text-[#D4AF37] text-lg mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('hourly_zurich_only_title') || 'Hourly Booking - Zurich Area Only'}
              </p>
              <p className="text-[#A1A1AA] text-sm mb-4">
                {t('hourly_zurich_only_msg') || 'Hourly booking is available only in the Zurich area. For special routes, please contact us:'}
              </p>
              <a
                href="https://wa.me/41799270731"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white font-medium text-sm uppercase tracking-wider hover:bg-[#25D366]/90 transition-colors"
              >
                WhatsApp: +41 79 927 07 31
              </a>
              <p className="text-[#A1A1AA] text-xs mt-3">
                {t('hourly_zurich_only_call') || 'Or call us at'} +41 79 927 07 31
              </p>
            </div>
          )}

          {/* Price Result */}
          {price !== null && (
            <div className="mt-8 p-6 bg-[#0A0A0A] border border-[#D4AF37]/30" data-testid="price-result">
              <div className="text-center">
                <p className="text-sm text-[#A1A1AA] mb-2">{t('calc_your_price')}</p>
                <p 
                  className="text-5xl font-light gold-text"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  data-testid="calculated-price"
                >
                  CHF {price.toFixed(2)}
                </p>
                
                {/* Price Details */}
                {priceDetails && (
                  <div className="mt-4 text-sm text-[#A1A1AA]">
                    {priceDetails.type === 'Airport Transfer' && (
                      <p>{t('calc_included')}: 45 min {t('calc_waiting')}</p>
                    )}
                    {priceDetails.type === 'Hourly' && (
                      <p>{t('calc_included')}: {priceDetails.km_included} {t('calc_km_included')}</p>
                    )}
                    <div className="flex items-center justify-center gap-3 mt-2 text-[#D4AF37]">
                      <Droplets className="w-4 h-4" />
                      <Wifi className="w-4 h-4" />
                      <span className="text-xs">{t('calc_perks')}</span>
                    </div>
                  </div>
                )}

                <Link
                  to={`/booking?service=${formData.service_type}&type=${formData.booking_type}&route=${formData.route_id}&hours=${formData.hours}&passengers=${formData.passengers}&price=${price}`}
                  className="inline-flex items-center gap-2 mt-6 px-8 py-3 bg-[#D4AF37] text-black font-semibold text-sm hover:bg-[#C5A059] transition-all duration-300"
                  data-testid="book-this-transfer"
                >
                  {t('calc_book_now')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PriceCalculator;
